import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import {
	useGetGeoCountriesMutation,
	useGetGeoStatesMutation,
	useGetGeoCitiesMutation,
	useGetGeoRegionsQuery,
	useLazyGetPositionsQuery,
	useGetIndustriesQuery,
	useLazyGetKeywordsQuery,
	useLazyGetTechnologiesQuery,
	useLazyGetCompaniesAutocompleteQuery,
} from "@/pages/Private/redux/companyEnrich/companyEnrich.api";
import { useLazyGeSuggestionQuery } from "@/pages/Private/redux/liPeople/liPeople.api";

import { CompanyEnrichAutoCompleteItem } from "./CompanyEnrichAutoComplete";
import { GeoRegion, Industry } from "../schema/companyEnrich";

type Fetcher = (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
type GeoFetcher = (
	query: string,
	countryCodes?: string[]
) => Promise<CompanyEnrichAutoCompleteItem[]>;

export interface CompanyEnrichLookups {
	countryNameMap: MutableRefObject<Map<string, string>>;
	fetchPositions: Fetcher;
	fetchCountries: Fetcher;
	fetchSchools: Fetcher;
	fetchKeywords: Fetcher;
	fetchTechnologies: Fetcher;
	fetchCompanyDomains: Fetcher;
	fetchCompanyCountries: Fetcher;
	fetchStates: GeoFetcher;
	fetchCities: GeoFetcher;
	industriesData: Industry[];
	industryOptions: CompanyEnrichAutoCompleteItem[];
	geoRegions: GeoRegion[];
	regionOptions: CompanyEnrichAutoCompleteItem[];
}

/**
 * Centralises the geo / industry / autocomplete lookups used by the Company Enrich form,
 * including preloading country names so ISO codes can be displayed as full names.
 */
export const useCompanyEnrichLookups = (): CompanyEnrichLookups => {
	const [getGeoCountries] = useGetGeoCountriesMutation();
	const [getGeoStates] = useGetGeoStatesMutation();
	const [getGeoCities] = useGetGeoCitiesMutation();
	const { data: geoRegionsData } = useGetGeoRegionsQuery();
	const [triggerGetPositions] = useLazyGetPositionsQuery();
	const { data: industriesData } = useGetIndustriesQuery();
	const [triggerGetKeywords] = useLazyGetKeywordsQuery();
	const [triggerGetTechnologies] = useLazyGetTechnologiesQuery();
	const [triggerGetCompaniesAutocomplete] = useLazyGetCompaniesAutocompleteQuery();
	const [triggerGetSuggestion] = useLazyGeSuggestionQuery();

	const countryNameMapRef = useRef<Map<string, string>>(new Map());
	const [, setCountryNameMapVersion] = useState(0);

	useEffect(() => {
		const preloadCountries = async () => {
			const pages = [1, 2, 3];
			const results = await Promise.all(pages.map((page) => getGeoCountries({ page }).unwrap()));

			for (const result of results) {
				for (const country of result.items) {
					countryNameMapRef.current.set(country.code, country.name);
				}
			}

			setCountryNameMapVersion((v) => v + 1);
		};

		preloadCountries();
	}, [getGeoCountries]);

	const fetchPositions = useCallback<Fetcher>(
		async (query) => {
			const result = await triggerGetPositions({ query }).unwrap();

			return result.map((item) => ({ id: item, title: item }));
		},
		[triggerGetPositions]
	);

	const fetchCountries = useCallback<Fetcher>(
		async (query) => {
			const result = await getGeoCountries({ query }).unwrap();

			return result.items.map((item) => ({ id: item.code, title: item.name }));
		},
		[getGeoCountries]
	);

	// LinkedIn school suggestions; the education filter stores the institution *name*
	// (free-text query upstream), so the title doubles as the item id.
	const fetchSchools = useCallback<Fetcher>(
		async (query) => {
			const result = await triggerGetSuggestion({ type: "school", keyword: query }).unwrap();
			const titles = [...new Set(result.suggestions.map((suggestion) => suggestion.title))];

			return titles.map((title) => ({ id: title, title }));
		},
		[triggerGetSuggestion]
	);

	const fetchKeywords = useCallback<Fetcher>(
		async (query) => {
			const result = await triggerGetKeywords({ query }).unwrap();

			return result.map((item) => ({ id: item, title: item }));
		},
		[triggerGetKeywords]
	);

	const fetchTechnologies = useCallback<Fetcher>(
		async (query) => {
			const result = await triggerGetTechnologies({ query }).unwrap();

			return result.map((item) => ({ id: item, title: item }));
		},
		[triggerGetTechnologies]
	);

	const fetchCompanyDomains = useCallback<Fetcher>(
		async (query) => {
			const result = await triggerGetCompaniesAutocomplete({ query }).unwrap();

			return result.map((item) => ({
				id: item.domain,
				title: item.name ? `${item.domain} (${item.name})` : item.domain,
				logoUrl: item.logoUrl,
			}));
		},
		[triggerGetCompaniesAutocomplete]
	);

	const fetchCompanyCountries = useCallback<Fetcher>(
		async (query) => {
			const result = await getGeoCountries({ query }).unwrap();

			return result.items.map((item) => ({ id: item.code, title: item.name }));
		},
		[getGeoCountries]
	);

	const fetchStates = useCallback<GeoFetcher>(
		async (query, countryCodes) => {
			if (!countryCodes || countryCodes.length === 0) {
				return [];
			}

			const result = await getGeoStates({ query, countryCodes }).unwrap();

			return result.items.map((item) => ({ id: String(item.id), title: item.name }));
		},
		[getGeoStates]
	);

	const fetchCities = useCallback<GeoFetcher>(
		async (query, countryCodes) => {
			if (!countryCodes || countryCodes.length === 0) {
				return [];
			}

			const result = await getGeoCities({ query, countryCodes }).unwrap();

			return result.items.map((item) => ({ id: String(item.id), title: item.name }));
		},
		[getGeoCities]
	);

	const industryOptions = (industriesData || []).map((item) => ({
		id: item.name,
		title: item.name,
	}));

	const regionOptions = (geoRegionsData?.items || []).map((item) => ({
		id: item.name,
		title: item.name,
	}));

	return {
		countryNameMap: countryNameMapRef,
		fetchPositions,
		fetchCountries,
		fetchSchools,
		fetchKeywords,
		fetchTechnologies,
		fetchCompanyDomains,
		fetchCompanyCountries,
		fetchStates,
		fetchCities,
		industriesData: industriesData || [],
		industryOptions,
		geoRegions: geoRegionsData?.items || [],
		regionOptions,
	};
};
